
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useEffect, useMemo, useState } from 'react';
import { ENV_WARNING_BAR_HEIGHT } from '../../../../base/components/EnviromentWarningBar/EnviromentWarningBar';
import { Flex } from '../../../../base/components/Flex';
import {
  CreateSessionPlaylistItemParamInput,
  SceneParameter,
} from '../../../../graphql/graphql-operations';
import { useLanguage } from '../../../../hooks/useChangeUserLanguage';
import { useTranslationPrefix } from '../../../../hooks/useTranslationPrefix';
import { compareByOrdinalAndProp } from '../../../../lib/compare';
import { getSceneParams } from '../../../components/PlaylistBuilder/playlistHooks';
import QrAdditionalParamsDialog from '../components/QrAdditionalParamsDialog';
import QrButton from '../components/QrButton';
import QrLangSelect from '../components/QrLangSelect';
import QrPageLogo from '../components/QrPageLogo';
import QrSceneCard, {
  QrSceneCardDefaultColor,
  QrSceneColors,
} from '../components/QrSceneCard';
import { QrScene } from '../LaunchCompanyQrPage';

const showLanguageSelect = (langParam: SceneParameter | null) =>
  (langParam?.values?.length || 0) > 1;

const sceneHasLang = (scene: QrScene, langId: string | null) => {
  if (!langId) {
    return true;
  }
  const langParam = scene.scene.parameters.find(
    (param) => param.name === 'lang',
  );
  if (!langParam) {
    return true;
  }
  return !!langParam.values.find((lang) => lang.id === langId);
};

export const validateRequiredQrSceneParams = (
  scene: QrScene,
  params: CreateSessionPlaylistItemParamInput[],
): boolean => {
  const requiredParams = scene.scene.parameters
    .filter((p) => p.required && p.values?.length > 1)
    .filter((p) => p.name !== 'lang'); //ignore lang that is always set

  if (!requiredParams.length) {
    //nothing required
    return true;
  }

  for (const required of requiredParams) {
    const param = params.find((p) => p.name === required.name && p.value);
    if (!param) {
      return false;
    }
  }

  return true;
};

interface Props {
  qrScenes: QrScene[];
  onNext: (data: {
    qrSceneId: string;
    params: CreateSessionPlaylistItemParamInput[];
  }) => void;
}

const QrSceneStep = ({ qrScenes, onNext }: Props) => {
  const { _t } = useTranslationPrefix("LaunchPlaylistQrPageNew");
  const theme = useTheme();
  const uiLanguage = useLanguage();
  const { enqueueSnackbar } = useSnackbar();
  const [selectedLanguage, setSelectedLanguage] = useState<string>();
  const isTablet = useMediaQuery(theme.breakpoints.up("sm"));
  const isTabletLandscape = useMediaQuery(theme.breakpoints.up("md"));
  const [selectedScene, setSelectedScene] = useState<{
    qrSceneId?: string;
    additionalParamsDialogOpen: boolean;
    additionelParams: CreateSessionPlaylistItemParamInput[];
  }>({ additionalParamsDialogOpen: false, additionelParams: [] });

  const qrScene = qrScenes?.find((s) => s.id === selectedScene.qrSceneId);

  const langData = useMemo(() => {
    if (qrScenes) {
      const scenes = qrScenes.map((s) => s.scene);
      const params = getSceneParams(scenes);
      const langParam = params?.find((param) => param.name === "lang") || null;
      let initialLang: string | null = null;
      if (langParam) {
        langParam.values.sort(compareByOrdinalAndProp("name"));
        initialLang =
          langParam.values.find((lang) =>
            lang.name?.toLowerCase().includes(uiLanguage)
          )?.id ||
          langParam.values?.[0]?.id ||
          null;
      }
      return { langParam, initialLang };
    }
    return {
      langParam: null,
      initialLang: null,
    };
  }, [qrScenes]);
  const language = selectedLanguage || langData.initialLang;

  const scenes = useMemo(() => {
    console.log("language: ", language);
    console.log("qrScenes: ", qrScenes);
    const scenes = qrScenes.filter((scene) => sceneHasLang(scene, language!));
    console.log("scenes: ", scenes);

    const sortedByName = scenes.sort((s1, s2) =>
      s1.scene.name_t.localeCompare(s2.scene.name_t, uiLanguage, {
        numeric: true,
      })
    );

    const sortedByColor = sortedByName.sort((s1, s2) => {
      const color1 = s1.scene.qrCardColor || QrSceneCardDefaultColor;
      const color2 = s2.scene.qrCardColor || QrSceneCardDefaultColor;
      const idx1 = QrSceneColors.indexOf(color1);
      const idx2 = QrSceneColors.indexOf(color2);
      if (idx1 < 0 && idx2 < 0) {
        //at least put same colors togenther
        return color1.localeCompare(color2, undefined, { numeric: true });
      }
      //unknown colors last
      if (idx1 < 0) {
        return 1;
      }
      if (idx2 < 0) {
        return -1;
      }

      return idx1 - idx2;
    });

    return sortedByColor;
  }, [qrScenes, language]);

  const handleSceneSelect = (qrSceneId: string) => () => {
    const qrScene = qrScenes?.find((s) => s.id === qrSceneId);
    if (!qrScene) {
      return;
    }
    setSelectedScene({
      qrSceneId: qrSceneId,
      additionelParams: [],
      additionalParamsDialogOpen: !validateRequiredQrSceneParams(qrScene, []),
    });
  };

  const handleNextClick = (params?: CreateSessionPlaylistItemParamInput[]) => {
    if (!qrScene) {
      enqueueSnackbar(
        _t("selectAppToLaunch ", "Select scene to launch first!"),
        {
          variant: "error",
        }
      );
      return;
    }
    let data = selectedScene;
    if (params) {
      setSelectedScene((s) => {
        data = { ...s, additionelParams: params || [] };
        return data;
      });
    }

    const valid = validateRequiredQrSceneParams(qrScene, data.additionelParams);
    if (!valid) {
      setSelectedScene((s) => ({ ...s, additionalParamsDialogOpen: true }));
      return;
    }
    params = [...data.additionelParams];

    const sceneLangParam = qrScene.scene.parameters.find(
      (p) => p.name === "lang"
    );
    if (sceneLangParam && sceneLangParam.values?.length) {
      const langValue =
        sceneLangParam.values.find((v) => v.id === language) ||
        sceneLangParam.values[0];

      params.push({
        name: sceneLangParam.name,
        value: langValue.name,
        paramId: sceneLangParam.id,
        valueId: langValue.id,
      });
    }
    onNext({ qrSceneId: qrScene.id, params: params });
  };

  //handle case where only one app and no language to select
  useEffect(() => {
    if (qrScenes?.length !== 1) {
      return;
    }
    const qrScene = qrScenes[0];

    if (showLanguageSelect(langData.langParam)) {
      //just set it as scene
      setSelectedScene({
        qrSceneId: qrScene.id,
        additionalParamsDialogOpen: false,
        additionelParams: [],
      });
      return;
    }
    const valid = validateRequiredQrSceneParams(qrScene, []);
    if (!valid) {
      //some required params open dialog
      setSelectedScene({
        qrSceneId: qrScene.id,
        additionalParamsDialogOpen: true,
        additionelParams: [],
      });
      return;
    }

    //no required params, prepare language and skip page
    const params: CreateSessionPlaylistItemParamInput[] = [];
    const sceneLangParam = qrScene.scene.parameters.find(
      (p) => p.name === "lang"
    );

    if (sceneLangParam && sceneLangParam.values?.length) {
      const langValue =
        sceneLangParam.values.find((v) => v.id === language) ||
        sceneLangParam.values[0];

      params.push({
        name: sceneLangParam.name,
        value: langValue.name,
        paramId: sceneLangParam.id,
        valueId: langValue.id,
      });
    }

    onNext({ qrSceneId: qrScene.id, params: params });
  }, []);

  const simpleApp = qrScenes.length <= 1;

  return (
    <Flex
      flexDirection="column"
      width="100%"
      minHeight="100%"
      alignItems="center"
      gap={2}
      px={{ xs: 2, md: 6 }}
    >
      <Flex
        flexDirection={{ xs: "column-reverse", md: "row" }}
        width="100%"
        justifyContent={{ xs: "center", md: "space-between" }}
        alignItems="center"
        gap={2}
      >
        <Box>
          {!simpleApp &&
            langData.langParam &&
            showLanguageSelect(langData.langParam) &&
            language && (
              <QrLangSelect
                param={langData.langParam}
                selectedId={language}
                onSelected={(lang) => {
                  setSelectedLanguage(lang);
                  setSelectedScene({
                    additionelParams: [],
                    additionalParamsDialogOpen: false,
                  });
                }}
                largeVersion={false}
              />
            )}
        </Box>
        <QrPageLogo />
      </Flex>

      {simpleApp ? (
        <Flex flexDirection="column" alignItems="center" gap={2} my="auto">
          <Typography textAlign="center">
            {_t("chooseAppLanguage", "Select application language.")}
          </Typography>
          {langData.langParam &&
            showLanguageSelect(langData.langParam) &&
            language && (
              <QrLangSelect
                param={langData.langParam}
                selectedId={language}
                onSelected={setSelectedLanguage}
                largeVersion={true}
              />
            )}
        </Flex>
      ) : (
        <Box
          display="grid"
          gridTemplateColumns={{
            xs: "repeat(2, minmax(0, 1fr))",
            sm: scenes.length < 2 ? undefined : "repeat(4, minmax(0, 1fr))",
            md:
              scenes.length < 3 || scenes.length === 4
                ? undefined
                : `repeat(6, minmax(0, 1fr))`,
          }}
          maxHeight={{
            xs: `calc(100dvh - 16rem - ${ENV_WARNING_BAR_HEIGHT})`,
            md: `calc(100dvh - 13rem - ${ENV_WARNING_BAR_HEIGHT})`,
          }}
          gap={1}
          my="auto"
          sx={{
            overflowY: "auto",
            scrollbarWidth: "thin",
          }}
        >
          {scenes?.map((qrs, idx, scenes) => (
            <>
              {isTabletLandscape &&
                scenes.length === 5 &&
                idx === scenes.length - 2 && (
                  <Box id="five scenes, center bottom row on tablet horizontaly"></Box>
                )}
              {!isTabletLandscape &&
                isTablet &&
                scenes.length === 5 &&
                idx === scenes.length - 1 && (
                  <Box id="five scenes, center bottom row on tablet vertically"></Box>
                )}
              {!isTabletLandscape &&
                isTablet &&
                scenes.length === 3 &&
                idx === scenes.length - 1 && (
                  <Box id="three scenes on tablet horizontaly center bottom row"></Box>
                )}
              <QrSceneCard
                onClick={handleSceneSelect(qrs.id)}
                selected={qrs.id === selectedScene?.qrSceneId}
                scene={qrs.scene}
                key={qrs.id}
                id={qrs.id}
                columns={scenes.length <= 2 || scenes.length === 4 ? 2 : 3}
                rows={scenes.length <= 3 ? 1 : 2}
                size={
                  isTabletLandscape && scenes.length < 4
                    ? "large"
                    : isTablet && scenes.length <= 6
                    ? "medium"
                    : "small"
                }
              />
            </>
          ))}
        </Box>
      )}
      <Flex sx={{ width: "100%" }} flexDirection="column" alignItems="center">
        <QrButton
          id="qr-next"
          color="primary"
          onClick={() => handleNextClick()}
        >
          {_t("sceneNext", "Launch")}
        </QrButton>
      </Flex>

      {selectedScene.qrSceneId && (
        <QrAdditionalParamsDialog
          open={selectedScene.additionalParamsDialogOpen}
          scene={qrScene}
          onConfirm={handleNextClick}
          onClose={() =>
            setSelectedScene((s) => ({
              ...s,
              additionalParamsDialogOpen: false,
            }))
          }
        />
      )}
    </Flex>
  );
};

export default QrSceneStep;
